import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
  NgForm,
} from '@angular/forms';
import { MatInput, MatInputModule } from '@angular/material/input';
import {
  MatFormFieldControl,
  MatFormFieldModule,
} from '@angular/material/form-field';

import { Subject } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ApiQueryParameters, GUID_REGEX, getPropertyForId } from 'utils';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { FocusMonitor } from '@angular/cdk/a11y';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ErrorStateMatcher } from '@angular/material/core';
import { CtxButtonComponent } from '../button/button.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
@Component({
  selector: 'ctx-customer-search',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    CtxButtonComponent,
  ],
  templateUrl: './customer-search.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: CtxCustomerSearchComponent,
    },
  ],
})
export class CtxCustomerSearchComponent
  implements
    MatFormFieldControl<string | null>,
    ControlValueAccessor,
    OnInit,
    OnDestroy
{
  constructor(
    public focusMonitor: FocusMonitor,
    @Optional() private _parentForm: NgForm,
    @Optional() private _parentFormGroup: FormGroupDirective,
    @Optional() @Self() public ngControl: NgControl,
    // @Optional() @Inject(MAT_FORM_FIELD) public formField: any,
    private _elementRef: ElementRef,
    public errorStateMatcher: ErrorStateMatcher
  ) {
    // NG_VALUE_ACCESSOR equivalent
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  /** Unique ID for the component */
  static nextId = 0;
  @HostBinding()
  id = `ctx-customer-search-${CtxCustomerSearchComponent.nextId++}`;

  /** Loading state for the async requests */
  loading = false;

  /** User name for current user */
  @Output() userName = new EventEmitter<string>();
  /** Ref to Autocomplete. Needed to set value OnInit. */
  @ViewChild(MatAutocompleteTrigger) _autocomplete: MatAutocompleteTrigger;

  @ViewChild(MatInput, { read: ElementRef, static: true })
  input: ElementRef<HTMLInputElement>;

  /** Store for searched users */
  searchedCustomers: any[] = [];

  @Input({ required: true }) type: 'user' | 'organization' | 'reseller';
  /** ResourceService.read */
  @Input({ required: true }) read: (
    search: string
  ) => Promise<HttpResponse<any>>;
  /** ResourceService.list */
  @Input({ required: true }) list: (
    page: number,
    size: number,
    params: ApiQueryParameters
  ) => Promise<HttpResponse<any[]>>;

  async writeValue(obj: any) {
    if (obj) {
      // Set the value to ID immediately
      this.value = obj;
      try {
        this.loading = true;
        const customer = await this.read(obj);
        this.searchedCustomers = [customer?.body];
        // Update the display value after loading
        this.value = this.displayUser(obj);
      } catch (error) {
        // Keep showing the ID if there's an error
        this.value = obj;
      } finally {
        this.loading = false;
      }
    } else {
      this.value = '';
    }
    this.onTouched();
  }

  // Standard boilerplate, thanks Angular
  onChange: any;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Tell mat-form-field about state changes
  stateChanges = new Subject<void>();
  controlType = 'ctx-customer-search';

  // the customerId, that is the value of this field
  private _customerId: string | null;
  @Input()
  get value(): string | null {
    return this._customerId;
  }
  set value(id: string | null) {
    this._customerId = id;
    this.stateChanges.next();
  }

  // Placeholder
  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  focused = false;

  get empty() {
    return this.value === null || this.value === '';
  }

  // Labels always float in this project
  shouldLabelFloat = true;

  @Input()
  get required() {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  get errorState(): boolean {
    return this.errorStateMatcher.isErrorState(
      this.ngControl.control,
      this._parentFormGroup
    );
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') userAriaDescribedBy: string;
  setDescribedByIds(ids: string[]) {
    ids;
  }

  // Some more boilerplate
  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this._elementRef.nativeElement.querySelector('input').focus();
    }
  }

  /**
   * Populate the searchedUsers array based on user input.
   */
  async searchCustomers(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    if (eventTarget && eventTarget.value) {
      try {
        this.loading = true;
        // Fetch 30 users from the Web API based on the value of the field.
        const customers = await this.listCustomers(eventTarget.value);
        this.searchedCustomers = customers?.body || [];
      } catch (error) {
        // Fallback to ID
        this._autocomplete.writeValue(eventTarget.value);
      }
      this.loading = false;
    }
  }
  /** Handler that returns appropriate funciton for type */
  async listCustomers(name: string) {
    if (this.type === 'user') {
      return this.list(1, 30, {
        role: 'user',
        query: name,
      });
    } else {
      return this.list(1, 30, {
        query: name,
      });
    }
  }

  /**
   * For User field and similar, a generic tearoff to show the User name after selection and not a GUID.
   * @param id User Id
   * @returns Selected user name, if found in the locally scoped filteredUsers, else the id.
   */
  displayUser = (id: string): string => {
    return getPropertyForId(this.searchedCustomers, id, 'name');
  };
  /** On option selection, set the control value */
  handleUserSelection(event: MatAutocompleteSelectedEvent) {
    this.onChange(event.option.value);
    this.userName.emit(this.displayUser(event.option.value));
  }

  ngOnInit(): void {
    if (this.input) {
      this.focusMonitor.monitor(this.input).subscribe((focused) => {
        this.focused = !!focused;
        this.stateChanges.next();
      });
    }
  }
  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.input);
    this.stateChanges.complete();
  }
  /** True if user is selected */
  get isUserSelected() {
    this.onTouched();
    return (
      this.ngControl &&
      typeof this.ngControl.value === 'string' &&
      GUID_REGEX.test(this.ngControl.value)
    );
  }

  /** Reset the control */
  clearUser() {
    if (this.ngControl?.value) {
      this.ngControl.reset('');
      this.onTouched();
    }
  }
}
